import {
  createAgent,
  updateAgent,
  AgentDetail,
  AgentCreate,
} from "../services/api";
import { StrategyConfigDraft } from "../types/strategyConfig";

// Extended interface for the agent operations
interface ExtendedAgentDetail extends Omit<AgentDetail, "tags"> {
  tag_ids?: number[];
  tags?: any;
  proactive_audio?: boolean;
  enable_affective_dialog?: boolean;
  strategy_config?: StrategyConfigDraft | null;
  latency_comparison_providers?: Partial<
    Record<"stt" | "llm" | "tts" | "v2v", string[]>
  >;
  pstn_provider?: string;
  live?: boolean;
  latency_alerts?: {
    enabled?: boolean;
    email_recipients?: string[];
    slack_email_recipients?: string[];
  };
}

/**
 * Creates a new agent with the given agent data
 * @param agentData - The agent data to create
 * @returns A promise that resolves when the agent is created
 */
export const createNewAgent = async (
  agentData: Partial<ExtendedAgentDetail>
): Promise<AgentDetail> => {
  console.log("Creating new agent:", agentData);

  // Ensure the API path is set correctly and remove any id field
  const agentToSave: AgentCreate = {
    name: agentData.name || "",
    description: agentData.description || "",
    startup_message:
      agentData.startup_message ||
      "Welcome to our service. How may I help you today?",
    voice_message: agentData.voice_message || "",
    system_prompt: agentData.system_prompt || "You are a helpful assistant...",
    enabled: agentData.enabled !== undefined ? agentData.enabled : true,
    live: agentData.live || false,
    is_outbound: agentData.is_outbound || false,
    primary_language_id: agentData.primary_language_id || 1,
    supported_language_ids: agentData.supported_language_ids || [],
    limitations: agentData.limitations || [],
    temperature: agentData.temperature ?? 0,
    tag_ids: agentData.tag_ids || [],
    model_name: agentData.model_name || "gemini-2.0-flash-live-001",
    access_token: agentData.access_token || "",
    phone_number: agentData.phone_number || "",
    pstn_provider: agentData.pstn_provider || "plivo",
    is_public: agentData.is_public || false,
    can_disconnect: agentData.can_disconnect || false,
    // Gemini 2.5 specific features
    proactive_audio: agentData.proactive_audio || false,
    enable_affective_dialog: agentData.enable_affective_dialog || false,
    // AI Strategy selection
    strategy: agentData.strategy || null,
    strategy_providers: agentData.strategy_providers || null,
    strategy_config: agentData.strategy_config || null,
    latency_comparison_providers: agentData.latency_comparison_providers || {},
    latency_alerts: agentData.latency_alerts || {
      enabled: true,
      email_recipients: [],
      slack_email_recipients: [],
    },
  } as AgentCreate;

  try {
    const result = await createAgent(agentToSave);
    return result;
  } catch (error: any) {
    console.error("Agent creation failed:", error);
    console.error("Error details:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Updates an existing agent with the given ID and agent data
 * @param agentUuid - The UUID of the agent to update
 * @param agentData - The updated agent data
 * @returns A promise that resolves when the agent is updated
 */
export const updateExistingAgent = async (
  agentUuid: string,
  agentData: Partial<ExtendedAgentDetail>
): Promise<AgentDetail> => {
  // Only send fields that are part of the AgentUpdate schema
  const updatedData = {
    uuid: agentData.uuid,
    name: agentData.name,
    description: agentData.description,
    startup_message: agentData.startup_message,
    voice_message: agentData.voice_message || "",
    system_prompt: agentData.system_prompt,
    enabled: agentData.enabled,
    live: agentData.live,
    is_outbound: agentData.is_outbound,
    phone_number: agentData.phone_number,
    pstn_provider: agentData.pstn_provider,
    temperature: agentData.temperature ?? 0,
    access_token: agentData.access_token,
    is_public: agentData.is_public,
    primary_language_id: agentData.primary_language_id,
    supported_language_ids: agentData.supported_language_ids || [],
    limitations: agentData.limitations || [],
    tag_ids: agentData.tag_ids || [],
    can_disconnect: agentData.can_disconnect,
    model_name: agentData.model_name,
    proactive_audio: agentData.proactive_audio,
    enable_affective_dialog: agentData.enable_affective_dialog,
    strategy: agentData.strategy,
    strategy_providers: agentData.strategy_providers,
    strategy_config: agentData.strategy_config,
    latency_comparison_providers: agentData.latency_comparison_providers || {},
    latency_alerts: agentData.latency_alerts || {
      enabled: true,
      email_recipients: [],
      slack_email_recipients: [],
    },
  };

  try {
    const result = await updateAgent(agentUuid, updatedData);
    return result;
  } catch (error: any) {
    console.error("Error updating agent:", error);
    console.error("Error details:", error.response?.data || error.message);
    throw error;
  }
};
